import React from "react";
import {
  Box,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { WifiOff } from "@mui/icons-material";
import VersionBadgeAndReload from "./VersionBadgeAndReload";

const NavigationPanel = ({
  menuItems,
  activeIndex,
  setActiveIndex,
  shouldRefresh,
  isReloading,
  clearCacheAndReload,
  navigate,
  setOpen,
  appVersion,
  checkboxes,
  cleaningCheckUrl,
  cleaningCheckDisabled,
}) => {
  return (
    <Box
      sx={{
        width: 280,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        <List component="nav" aria-label="navigation">
          {(menuItems || []).map((item, index) => {
            const isDisabled = !!item.disabled;
            const isSelected = index === activeIndex && !item.externalUrl;

            const handleClick = () => {
              if (isDisabled) return;

              if (item.externalUrl) {
                window.open(item.externalUrl, "_blank", "noopener,noreferrer");
              } else {
                setActiveIndex(index);
                navigate?.(index);
              }
              setOpen(false);
            };

            return (
              <ListItemButton
                divider
                key={`menu-${index}`}
                selected={isSelected}
                disabled={isDisabled}
                aria-current={isSelected ? "page" : undefined}
                sx={{
                  position: "relative",
                  alignItems: "flex-start",
                  "&.Mui-selected": {
                    bgcolor: "action.selected",
                  },
                  ...(isDisabled && {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  }),
                }}
                onClick={handleClick}
              >
                <ListItemText
                  primary={
                    item.isNew ? (
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        <Box component="span" sx={{ flex: 1 }}>
                          {item.title}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: "#d81939",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          New
                        </Box>
                      </Box>
                    ) : (
                      item.title
                    )
                  }
                  primaryTypographyProps={{
                    sx: {
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    },
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
        {cleaningCheckUrl && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              py: 1,
              px: 2,
              gap: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {cleaningCheckDisabled ? (
                <span
                  style={{
                    color: "rgba(0, 0, 0, 0.38)",
                    cursor: "not-allowed",
                    opacity: 0.5,
                  }}
                >
                  Cleaning check
                </span>
              ) : (
                <a
                  href={cleaningCheckUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Cleaning check
                </a>
              )}
              {cleaningCheckDisabled && (
                <WifiOff
                  sx={{ fontSize: 16, color: "text.disabled", opacity: 0.5 }}
                />
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "11px",
                textAlign: "center",
                lineHeight: 1.3,
              }}
            >
              Flight data will be prefilled if you're authenticated in Zendesk.
            </Typography>
          </Box>
        )}
      </Box>

      {(checkboxes || []).length > 0 && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Divider sx={{ mb: 1 }} />
          {(checkboxes || []).map((checkbox, index) => (
            <FormControlLabel
              key={`checkbox-${index}`}
              control={
                <Checkbox
                  checked={!!checkbox.checked}
                  onChange={(e, checked) =>
                    checkbox.handleChecked?.(checkbox, checked)
                  }
                />
              }
              label={checkbox.label}
            />
          ))}
        </Box>
      )}

      <Divider />
      <Grid
        container
        justifyContent={"center"}
        alignItems={"center"}
        sx={{ py: 0.5, px: 2 }}
      >
        <Grid item sx={{ mr: 1 }}>
          <Typography
            color={shouldRefresh ? "error" : "text.secondary"}
            variant="caption"
          >
            {shouldRefresh
              ? "New version available"
              : `Latest version v${appVersion}`}
          </Typography>
        </Grid>
        <Grid item>
          <VersionBadgeAndReload
            shouldRefresh={shouldRefresh}
            isReloading={isReloading}
            clearCacheAndReload={clearCacheAndReload}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default NavigationPanel;
export { NavigationPanel };
